import {
  ImageType,
  PointOfInterestTemplateField,
  TemplateDataInput,
  UnitsEnum,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import { TemplateDataHookKeys } from './hooks/useTemplateFields';
import { Maybe, PointOfInterest } from '@/__generated__/graphql';
import { localiseMeasurement } from '@/utils/unitSystem';
import { NOT_APPLICABLE_LABEL, UNCERTAIN_PROFILE_LABEL } from '@/constants';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { ImageWithId } from '@/types';

export const convertTemplateStateToTemplateData = (
  data: TemplateDataHookKeys
): TemplateDataInput[] => {
  const result: TemplateDataInput[] = Object.entries(data).map(([key, value]) => ({
    id: key,
    value: value || '',
  }));

  return result;
};
export const convertTemplateDataIntoFieldsArray = ({
  selectedPoi,
  unitSystem,
  templateFields,
}: {
  selectedPoi: PointOfInterest | undefined;
  unitSystem: UnitSystemEnum;
  templateFields: PointOfInterestTemplateField[];
}): Array<{
  label: string;
  value: string;
}> => {
  const values = templateFields?.map((field) => {
    const value = selectedPoi?.templateData?.find((td) => td.id === field.id)?.value || 'N/A';

    return {
      value,
      label: field?.name || 'N/A',
    };
  });

  if (selectedPoi?.blister) {
    const {
      maxHeight,
      estimatedHeight,
      pdrHeight,
      wallLoss,
      residualCorrosionAllowance,
      residualThickness,
    } = selectedPoi.blister;
    return [
      {
        value:
          localiseMeasurement({
            unitSystem,
            value: maxHeight || undefined,
            sourceUnit: UnitsEnum.Meter,
            displayAsUnit: UnitsEnum.Millimeter,
            showUnitLabel: true,
            fallbackLabel: UNCERTAIN_PROFILE_LABEL,
          }) || UNCERTAIN_PROFILE_LABEL,
        label: 'Blister Height',
      },
      {
        value:
          localiseMeasurement({
            unitSystem,
            value: estimatedHeight || undefined,
            sourceUnit: UnitsEnum.Meter,
            displayAsUnit: UnitsEnum.Millimeter,
            showUnitLabel: true,
          }) || UNCERTAIN_PROFILE_LABEL,
        label: 'Estimated Blister Height',
      },
      {
        value:
          localiseMeasurement({
            unitSystem,
            value: pdrHeight || undefined,
            sourceUnit: UnitsEnum.Meter,
            displayAsUnit: UnitsEnum.Millimeter,
            showUnitLabel: true,
          }) || UNCERTAIN_PROFILE_LABEL,
        label: 'PDR Height',
      },
      {
        value:
          localiseMeasurement({
            unitSystem,
            value: wallLoss?.value,
            sourceUnit: UnitsEnum.Meter,
            displayAsUnit: UnitsEnum.Millimeter,
            showUnitLabel: true,
          }) || NOT_APPLICABLE_LABEL,
        label: 'Estimated Wall Loss',
      },
      {
        value:
          localiseMeasurement({
            unitSystem,
            value: residualCorrosionAllowance?.value,
            sourceUnit: UnitsEnum.Meter,
            displayAsUnit: UnitsEnum.Millimeter,
            showUnitLabel: true,
          }) || NOT_APPLICABLE_LABEL,
        label: 'Residual Corrosion Allowance',
      },
      {
        value:
          localiseMeasurement({
            unitSystem,
            value: residualThickness?.value,
            sourceUnit: UnitsEnum.Meter,
            displayAsUnit: UnitsEnum.Millimeter,
            showUnitLabel: true,
          }) || NOT_APPLICABLE_LABEL,
        label: 'Residual Thickness',
      },
      ...values,
    ];
  }

  return values;
};

export const convertToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('load', () => {
      // Ensure the result is a string before resolving
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Result of file reading was not a string.'));
      }
    });
    reader.addEventListener('error', () => reject(reader.error));
  });

export const generatePoiDisplayName = ({
  name,
  poiId,
  poiType,
}: {
  name: Maybe<string> | undefined;
  poiId: Maybe<string> | undefined;
  poiType: string | undefined;
}) => {
  if (!poiId) return name || poiType || 'N/A';

  if (!name && !poiType) return `POI - ${poiId}`;

  return `${name || poiType} - ${poiId}`;
};

export const mapAttachmentType = (
  currentImage: ImageWithId | undefined,
): Attachment | null => {
  if (!currentImage) return null;

  return {
    caption: currentImage.caption ?? undefined,
    id: currentImage.id ?? undefined,
    isNew: false,
    imageType: ImageType.Other,
    name: currentImage.name ?? '',
    resourcePath: currentImage.src,
    uuid: currentImage.uuid ?? undefined,
    type: 'others',
    fileName:currentImage.fileName
  };
};
